import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { PRODUCTS, GALLERY, waLink } from "../data/site";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

export default function Products() {
  usePageMeta(
    "Products | Leaf Plates, Bowls, Thalis & Grass Straws | ORGANIKTHINGS LLP",
    "Browse the ORGANIKTHINGS LLP eco-friendly catalogue: green leaf plates, leaf bowls and dona-pattal, leaf thalis and grass straws. Request bulk pricing for your business."
  );
  return (
    <main data-testid="products-page">
      <section className="bg-[#f4f1ea] py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold tracking-wider uppercase text-[#2d6a4f] mb-3">Product Catalogue</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0d1f18] leading-tight">
            Our eco-friendly product range
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#2d3748] leading-relaxed max-w-2xl">
            Natural leaf plates, bowls, thalis and grass straws for caterers,
            hotels, restaurants, dealers and distributors. Every product is
            available for bulk and wholesale enquiry — pricing is shared
            directly with business buyers, never published online.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20" data-testid="products-catalogue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.id} delay={i * 80} className="h-full">
                <ProductCard product={p} large />
              </Reveal>
            ))}
          </div>

          {/* In-use band */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Reveal>
              <figure className="rounded-2xl overflow-hidden border border-[#e2ded4]">
                <img
                  src={GALLERY.bowlServing}
                  alt="Leaf bowl serving dal at a restaurant table"
                  className="w-full h-72 object-cover"
                  loading="lazy"
                  data-testid="products-serving-bowl"
                />
                <figcaption className="bg-[#faf9f6] px-5 py-3 text-sm text-[#5a6b5c]">
                  Leaf bowls in everyday food service
                </figcaption>
              </figure>
            </Reveal>
            <Reveal delay={80}>
              <figure className="rounded-2xl overflow-hidden border border-[#e2ded4]">
                <img
                  src={GALLERY.thaliMeal}
                  alt="Full biryani meal served on a natural leaf thali"
                  className="w-full h-72 object-cover"
                  loading="lazy"
                  data-testid="products-serving-meal"
                />
                <figcaption className="bg-[#faf9f6] px-5 py-3 text-sm text-[#5a6b5c]">
                  A full meal served on a leaf thali
                </figcaption>
              </figure>
            </Reveal>
            <Reveal delay={160}>
              <figure className="rounded-2xl overflow-hidden border border-[#e2ded4]">
                <img
                  src={GALLERY.thaliSpread}
                  alt="Leaf thalis and bowls with full Indian meals"
                  className="w-full h-72 object-cover"
                  loading="lazy"
                  data-testid="products-serving-thali"
                />
                <figcaption className="bg-[#faf9f6] px-5 py-3 text-sm text-[#5a6b5c]">
                  Thalis and bowls for complete meal service
                </figcaption>
              </figure>
            </Reveal>
          </div>

          {/* Bulk CTA */}
          <Reveal>
            <div className="mt-14 bg-[#1b4332] rounded-2xl p-8 md:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 text-white">
              <div className="max-w-2xl">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
                  Bulk & wholesale supply
                </h2>
                <p className="mt-3 text-base text-white/80 leading-relaxed">
                  We supply businesses, dealers and distributors directly. Share
                  your product and quantity requirement and we'll get back with
                  bulk pricing and availability.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Link
                  to="/contact"
                  data-testid="products-bulk-cta"
                  className="inline-flex justify-center items-center gap-2 bg-white text-[#1b4332] hover:bg-[#d8f3dc] font-semibold px-6 py-3 rounded-lg transition-colors"
                >
                  Get a Business Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={waLink("Hello Organik Things, I would like bulk pricing for your products.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="products-whatsapp-cta"
                  className="inline-flex justify-center items-center gap-2 border border-white/40 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp Us
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
